import { useState, useEffect, useCallback } from 'react';
import { settingsAPI } from '../services/api';

const HIJRI_MONTHS = [
  'Muharram', 'Safar', 'Rabi al-Awwal', 'Rabi al-Thani',
  'Jumada al-Awwal', 'Jumada al-Thani', 'Rajab', 'Shaaban',
  'Ramadan', 'Shawwal', 'Dhul Qadah', 'Dhul Hijjah',
];

const LEAP_REMAINDERS = new Set([2, 5, 7, 10, 13, 16, 18, 21, 24, 26, 29]);

function isLeapYear(year) {
  return LEAP_REMAINDERS.has(year % 30);
}

function daysInMonth(month, year) {
  if (month === 12 && isLeapYear(year)) return 30;
  return month % 2 === 1 ? 30 : 29;
}

function calcTodayHijri(refGregorian, refDay, refMonth, refYear) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const ref = new Date(refGregorian + 'T00:00:00');
  const daysDiff = Math.round((today - ref) / 86_400_000);

  let day = refDay + daysDiff;
  let month = refMonth;
  let year = refYear;

  while (day > daysInMonth(month, year)) {
    day -= daysInMonth(month, year);
    month++;
    if (month > 12) { month = 1; year++; }
  }
  while (day <= 0) {
    month--;
    if (month <= 0) { month = 12; year--; }
    day += daysInMonth(month, year);
  }

  return { day, month, year, monthName: HIJRI_MONTHS[month - 1] };
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

export default function SiteSettings() {
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [error,   setError]     = useState('');
  const [success, setSuccess]   = useState('');

  const [refDate,  setRefDate]  = useState(todayStr());
  const [day,      setDay]      = useState(1);
  const [month,    setMonth]    = useState(1);
  const [year,     setYear]     = useState(1447);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await settingsAPI.get();
      const s = data.data;
      if (s) {
        setRefDate(s.hijri_reference_gregorian?.slice(0, 10) ?? todayStr());
        setDay(s.hijri_day);
        setMonth(s.hijri_month);
        setYear(s.hijri_year);
      }
    } catch {
      setError('Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await settingsAPI.updateHijri({
        hijri_day: Number(day),
        hijri_month: Number(month),
        hijri_year: Number(year),
        hijri_reference_gregorian: refDate,
      });
      setSuccess('Hijri date reference saved successfully.');
    } catch (err) {
      setError(err.response?.data?.message ?? 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  const preview = (() => {
    try {
      return calcTodayHijri(refDate, Number(day), Number(month), Number(year));
    } catch {
      return null;
    }
  })();

  const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 14,
    border: '1px solid var(--border)', background: 'var(--surface)',
    color: 'var(--text)', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 5, display: 'block' };

  return (
    <div style={{ padding: 28, maxWidth: 560 }}>
      <h2 style={{ fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 700, margin: '0 0 4px' }}>
        Site Settings
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '0 0 24px' }}>
        Set the Hijri date reference. The app auto-counts forward from this point.
      </p>

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : (
        <form onSubmit={handleSave}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>
              Hijri Date Reference
            </div>

            {/* Reference Gregorian Date */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>
                Gregorian Date (what day does this Hijri date fall on?)
              </label>
              <input
                type="date"
                value={refDate}
                onChange={e => setRefDate(e.target.value)}
                style={inputStyle}
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 12, marginBottom: 8 }}>
              {/* Day */}
              <div>
                <label style={labelStyle}>Hijri Day</label>
                <input
                  type="number" min="1" max="30"
                  value={day}
                  onChange={e => setDay(e.target.value)}
                  style={inputStyle}
                  required
                />
              </div>

              {/* Month */}
              <div>
                <label style={labelStyle}>Hijri Month</label>
                <select
                  value={month}
                  onChange={e => setMonth(e.target.value)}
                  style={inputStyle}
                  required
                >
                  {HIJRI_MONTHS.map((name, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}. {name}</option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div>
                <label style={labelStyle}>Hijri Year (AH)</label>
                <input
                  type="number" min="1300" max="1600"
                  value={year}
                  onChange={e => setYear(e.target.value)}
                  style={inputStyle}
                  required
                />
              </div>
            </div>
          </div>

          {/* Live preview */}
          {preview && (
            <div style={{
              background: 'var(--accent-light, #eaf7ee)', border: '1px solid var(--accent, #3a6e3a)',
              borderRadius: 10, padding: '12px 16px', marginBottom: 20,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>
                Today's Hijri Date (preview)
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent)' }}>
                {preview.day} {preview.monthName} {preview.year} AH
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Calculated from your reference point above
              </div>
            </div>
          )}

          {error   && <div style={{ color: '#c0392b', fontSize: 13, marginBottom: 12 }}>{error}</div>}
          {success && <div style={{ color: '#27ae60', fontSize: 13, marginBottom: 12 }}>{success}</div>}

          <button
            type="submit"
            disabled={saving}
            style={{
              background: 'var(--accent)', color: '#fff', border: 'none',
              borderRadius: 8, padding: '10px 24px', fontSize: 14,
              fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save Reference'}
          </button>
        </form>
      )}
    </div>
  );
}
